import { useMemo } from "react";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import { Users, GraduationCap, Sparkles, ArrowUpRight, BarChart3, Compass, Rocket, Download } from "lucide-react";
import AiInsights from "@/components/schools/AiInsights";
import DemoFlow from "@/components/schools/DemoFlow";
import SchoolKpi from "@/components/schools/SchoolKpi";
import SchoolFilters, { useSchoolFilters, termLabel } from "@/components/schools/SchoolFilters";
import HeatmapLegend from "@/components/schools/HeatmapLegend";
import {
  school, kpis, grades, moduleCompletion, hdiHeatmap, careerInterests, futureSkills, studios, aiInsightsPrincipal,
} from "@/data/greenwood";
import { cn } from "@/lib/utils";
import { downloadCsv } from "@/lib/csv";
import { useDynamicInsights } from "@/hooks/useDynamicInsights";

// Anonymised peer-school benchmarks (avg of comparable IB/international schools).
const peerBenchmarks = {
  activeStudents: 1050,
  assessmentCompletion: 81,
  humanDevelopmentIndex: 76,
  futureReadinessIndex: 65,
} as const;

function heatColor(v: number) {
  // High → deep green, mid → soft mint, low → blush pink (chart-4).
  if (v >= 85) return "bg-primary text-primary-foreground";
  if (v >= 78) return "bg-primary/75 text-primary-foreground";
  if (v >= 70) return "bg-[hsl(var(--chart-2))]/70";
  if (v >= 62) return "bg-[hsl(var(--chart-3))]/70";
  return "bg-[hsl(var(--chart-4))]/70";
}


const maxInterest = Math.max(...careerInterests.map((c) => c.value));

export default function PrincipalDashboard() {
  const [filters] = useSchoolFilters();
  const principalInsights = useDynamicInsights(aiInsightsPrincipal, "principal");

  // Grade filter subsets the grades shown in the module completion table + heatmap columns.
  const gradeFilterStr = filters.grade === "all" ? null : filters.grade;
  const shownGrades = useMemo(
    () => (gradeFilterStr ? grades.filter((g) => String(g.grade) === gradeFilterStr) : grades),
    [gradeFilterStr]
  );
  const heatmapGradeIdx = useMemo(
    () => hdiHeatmap.grades.map((g, i) => ({ g, i })).filter(({ g }) => gradeFilterStr ? String(g) === gradeFilterStr : true),
    [gradeFilterStr]
  );

  const exportSnapshot = () => {
    const rows: (string | number)[][] = [
      ["WeKIT Education Intelligence™ — Principal snapshot"],
      ["School", school.name],
      ["Principal", school.principal],
      ["Academic Year", school.academicYear],
      ["Filters", `Grade: ${filters.grade}`, `House: ${filters.house}`, `Term: ${termLabel(filters.term)}`],
      ["Generated", new Date().toISOString()],
      [],
      ["KPI", "Value", "Peer benchmark"],
      ["Active students", kpis.activeStudents, peerBenchmarks.activeStudents],
      ["Assessment completion (%)", kpis.assessmentCompletion, peerBenchmarks.assessmentCompletion],
      ["Human Development Index™", kpis.humanDevelopmentIndex, peerBenchmarks.humanDevelopmentIndex],
      ["Future Readiness Index™", kpis.futureReadinessIndex, peerBenchmarks.futureReadinessIndex],
      [],
      ["HDI heatmap · dimension × grade"],
      ["Dimension", ...heatmapGradeIdx.map(({ g }) => `G${g}`)],
      ...hdiHeatmap.dimensions.map((dim, i) => [dim, ...heatmapGradeIdx.map(({ i: gi }) => hdiHeatmap.values[i][gi])]),
      [],
      ["Career interest cluster", "% students"],
      ...careerInterests.map((c) => [c.cluster, c.value]),
    ];
    downloadCsv(`greenwood-principal-snapshot-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  return (
    <>
      <PageHead title={`${school.name} | Principal Dashboard`} description="Executive dashboard for WeKIT Education Intelligence™" path="/schools" />
      <div className="container max-w-7xl py-8 space-y-10">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="schools-eyebrow flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> WeKIT Education Intelligence™ · Executive View · {termLabel(filters.term)}
            </div>

            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{school.name}</h1>
            <p className="text-muted-foreground mt-1">
              {school.principal} · Academic Year {school.academicYear} · {school.location}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="rounded-full">{school.students.toLocaleString()} students</Badge>
            <Badge variant="secondary" className="rounded-full">{school.teachers} teachers</Badge>
            <Badge variant="secondary" className="rounded-full">{school.counsellors} counsellors</Badge>
            <Button size="sm" variant="outline" onClick={exportSnapshot}>
              <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Export CSV
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/schools/live">Live tenant <ArrowUpRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" /></Link>
            </Button>
          </div>
        </header>

        <SchoolFilters />

        {/* Top KPI cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SchoolKpi variant="north-star" infoKey="hdi" icon={<Sparkles className="w-4 h-4" />} label="North Star · Institution HDI™" value={kpis.humanDevelopmentIndex} hint="School-wide Human Development Index" series={[76, 78, 79, 81, 82, 83, 84]} benchmark={peerBenchmarks.humanDevelopmentIndex} benchmarkSuffix=" pts" />
          <SchoolKpi infoKey="completion" icon={<GraduationCap className="w-4 h-4" />} label="Assessment completion"    value={`${kpis.assessmentCompletion}%`}      hint="WCC360 across all grades" tone="positive"          series={[78, 81, 84, 87, 89, 91, 92]} benchmark={peerBenchmarks.assessmentCompletion} benchmarkSuffix="%" />
          <SchoolKpi infoKey="active" icon={<Users className="w-4 h-4" />}         label="Active students"          value={kpis.activeStudents.toLocaleString()} hint={`${school.students.toLocaleString()} enrolled`}                                          series={[1102, 1128, 1141, 1160, 1172, 1181, 1187]} benchmark={peerBenchmarks.activeStudents} />
          <SchoolKpi infoKey="fri" icon={<Rocket className="w-4 h-4" />}        label="Future Readiness Index™"  value={kpis.futureReadinessIndex}            hint="+4 vs last term"          tone="positive"          series={[64, 66, 68, 69, 70, 72, 73]} benchmark={peerBenchmarks.futureReadinessIndex} benchmarkSuffix=" pts" />
        </section>

        {/* Grade × module completion */}
        <section className="space-y-3">
          <SectionHeader icon={<BarChart3 className="w-4 h-4" />} title="Grade & module completion" subtitle={gradeFilterStr ? `Filtered to Grade ${gradeFilterStr}` : "Assessment and Learning Studio completion by grade"} />
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th scope="col" className="p-3 font-medium">Module</th>
                    {shownGrades.map((g) => (
                      <th scope="col" key={g.grade} className="p-3 font-medium text-center">Grade {g.grade}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {moduleCompletion.map((row) => (
                    <tr key={row.module} className="border-t">
                      <th scope="row" className="p-3 font-medium text-left">{row.module}</th>
                      {shownGrades.map((g) => {
                        const v = (row as any)[g.grade] as number;
                        return (
                          <td key={g.grade} className="p-3 text-center">
                            <div className="inline-flex flex-col items-center gap-1 min-w-[64px]" aria-label={`${row.module} Grade ${g.grade}: ${v} percent`}>
                              <span className="text-sm font-semibold">{v}%</span>
                              <div className="w-14 h-1 bg-muted rounded-full overflow-hidden" role="presentation">
                                <div className="h-full bg-primary" style={{ width: `${v}%` }} />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>

        {/* Institution Intelligence grid */}
        <section className="space-y-3">
          <SectionHeader icon={<Compass className="w-4 h-4" />} title="Institution Intelligence" subtitle="How the school is growing beyond academics" />
          <div className="grid lg:grid-cols-2 gap-4">
            {/* HDI Heatmap */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Human Development Heatmap</CardTitle>
                <p className="text-xs text-muted-foreground">Dimension × grade · higher is stronger</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <caption className="sr-only">Human Development Index scores by dimension and grade</caption>
                    <thead>
                      <tr>
                        <th scope="col" className="p-2 text-left font-medium text-muted-foreground">Dimension</th>
                        {heatmapGradeIdx.map(({ g }) => (
                          <th scope="col" key={g} className="p-2 text-center font-medium text-muted-foreground">G{g}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hdiHeatmap.dimensions.map((dim, i) => (
                        <tr key={dim}>
                          <th scope="row" className="p-2 font-medium text-left">{dim}</th>
                          {heatmapGradeIdx.map(({ g, i: gi }) => {
                            const v = hdiHeatmap.values[i][gi];
                            return (
                              <td key={gi} className="p-1.5">
                                <div
                                  className={cn("h-9 rounded-lg grid place-items-center text-xs font-semibold", heatColor(v))}
                                  role="img"
                                  aria-label={`${dim} for Grade ${g}: ${v} out of 100`}
                                >
                                  {v}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <HeatmapLegend />
              </CardContent>
            </Card>

            {/* Career Interest Distribution */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Career Interest Distribution</CardTitle>
                <p className="text-xs text-muted-foreground">Share of students exploring each cluster</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {careerInterests.map((c) => (
                  <div key={c.cluster} className="grid grid-cols-[1fr_auto] items-center gap-3">
                    <div>
                      <div className="text-sm font-medium">{c.cluster}</div>
                      <div className="h-1.5 mt-1 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${(c.value / maxInterest) * 100}%` }} />
                      </div>
                    </div>
                    <span className="text-sm tabular-nums text-muted-foreground w-10 text-right">{c.value}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Future Skills Overview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Future Skills Overview</CardTitle>
                <p className="text-xs text-muted-foreground">School-wide progress on FutureReadySkills™</p>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                {futureSkills.map((s) => (
                  <div key={s.skill} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{s.skill}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {s.score}
                        <span className="ml-1 text-emerald-600 text-xs">+{s.delta}</span>
                      </span>
                    </div>
                    <Progress value={s.score} className="h-1.5" />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Learning Studio Adoption */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Learning Studio Adoption</CardTitle>
                <p className="text-xs text-muted-foreground">Active learners across WeKIT studios</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {studios.map((s) => (
                  <div key={s.name} className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                      <span className="text-xs font-bold">{s.name}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium truncate">{s.full}</div>
                        <div className="text-sm tabular-nums font-semibold">{s.adoption}%</div>
                      </div>
                      <div className="text-xs text-muted-foreground mb-1.5">{s.active.toLocaleString()} active · {s.modules} modules</div>
                      <Progress value={s.adoption} className="h-1.5" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* AI Insights */}
        <AiInsights
          subtitle="Evidence-based signals from this week's data"
          insights={principalInsights.insights}
          contextLabel={principalInsights.contextLabel}
        />

        <DemoFlow />
      </div>
    </>
  );
}


function SectionHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary grid place-items-center">{icon}</div>
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}
